<template>
  <!-- 新着ヤギオファーのブラウザ通知の ON/OFF。PC・ログイン中・マイページ配下だけに出す。
       初回（未設定・未許可）は案内を開いた状態、それ以外は右下の小さなボタンに畳む -->
  <div v-if="visible" class="offer-web-push">
    <div v-if="expanded" class="offer-web-push__card">
      <button
        type="button"
        class="offer-web-push__close"
        aria-label="閉じる"
        @click="collapse"
      >
        ×
      </button>
      <p class="offer-web-push__title">新着オファーをブラウザでお知らせ</p>
      <p class="offer-web-push__text">
        新しいヤギオファーが届いたら、このPCのブラウザに通知します。ブラウザのタブを閉じていても届きます。
      </p>
      <p
        v-if="permission === 'denied'"
        class="offer-web-push__text offer-web-push__text--warn"
      >
        ブラウザの設定で通知がブロックされています。ブラウザのサイト設定から
        yagish の通知を許可してください。
      </p>
      <div v-else class="offer-web-push__row">
        <span>ブラウザ通知</span>
        <el-switch
          :value="enabled"
          :disabled="busy"
          @change="onToggle"
        ></el-switch>
      </div>
      <p v-if="statusText" class="offer-web-push__status">{{ statusText }}</p>
    </div>
    <button
      v-else
      type="button"
      class="offer-web-push__pill"
      @click="expandedByUser = true"
    >
      オファーのブラウザ通知：{{ enabled ? 'オン' : 'オフ' }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { useRoute } from 'vue-router/composables'
import { useAuth } from '@/composables/useAuth'
import { useWebPush } from '@/composables/useWebPush'

const route = useRoute()
const { isAuthenticated } = useAuth()
const { available, enabled, permission, enable, disable } = useWebPush()

// 「今はしない」を選んだら、次からは案内を開かずボタンに畳んでおく
const dismissed = useLocalStorage('web_push_prompt_dismissed', false)
const expandedByUser = ref<boolean | null>(null)
const busy = ref(false)
const statusText = ref('')

const visible = computed(
  () =>
    available.value && isAuthenticated.value && route.path.startsWith('/mypage')
)
const expanded = computed(
  () =>
    expandedByUser.value ??
    (!enabled.value && !dismissed.value && permission.value === 'default')
)

function collapse() {
  expandedByUser.value = false
  dismissed.value = true
}

async function onToggle(next: boolean) {
  // 操作したら結果の表示が消えないよう開いたままにする
  expandedByUser.value = true
  busy.value = true
  statusText.value = ''
  try {
    if (!next) {
      await disable()
      statusText.value = 'ブラウザ通知をオフにしました'
      return
    }
    const result = await enable()
    statusText.value =
      result === 'enabled'
        ? 'ブラウザ通知をオンにしました'
        : result === 'denied'
          ? ''
          : '通知の設定に失敗しました。時間をおいてもう一度お試しください'
  } finally {
    busy.value = false
  }
}
</script>

<style lang="scss" scoped>
.offer-web-push {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 90;
  max-width: 320px;

  &__card {
    position: relative;
    padding: 16px 16px 12px;
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgb(0 0 0 / 12%);
  }

  &__close {
    position: absolute;
    top: 6px;
    right: 8px;
    font-size: 18px;
    line-height: 1;
    color: #999;
    cursor: pointer;
    background: none;
    border: none;
  }

  &__title {
    margin: 0 16px 8px 0;
    font-weight: bold;
  }

  &__text {
    margin: 0 0 12px;
    font-size: 13px;
    line-height: 1.6;
    color: #555;

    &--warn {
      color: #e3165c;
    }
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__status {
    margin: 8px 0 0;
    font-size: 12px;
    color: #555;
  }

  &__pill {
    padding: 8px 14px;
    font-size: 13px;
    color: #333;
    cursor: pointer;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 999px;
    box-shadow: 0 2px 8px rgb(0 0 0 / 10%);
  }
}
</style>
